import { Injectable } from "@angular/core";
import { IPagination } from "app/common/interfaces";
import {
  ExportContext,
  ExportFileFormat,
  ExportRange,
  ExportService,
  ExportTaskProcessor,
} from "app/layout/export-overlay";
import { IFilters } from "app/modules/components/filters/interfaces/filterInterface";
import { OrderCustomerService } from "app/modules/components/reports/historic/order-customer/order-customer.service";
import { lossInfo } from "app/modules/components/reports/loss-report/dto/loss-report.dto";
import { get } from "lodash-es";
import { formatLuxonDate } from "app/modules/components/sales/order-list-components/exports/sales-orders-list.utils";
import { takeUntil } from "rxjs";
import {
  CURRENCYFIELDS,
  FORMATTER,
  LOSS_PROPERTIES,
} from "./loss-report.constants";

@Injectable()
export class LossReportExport {
  constructor(
    private exportService: ExportService,
    private orderCustomerService: OrderCustomerService
  ) {}

  UMPACTIVE: boolean = false;

  private formatQuantityUMB(quantity?: number): string {
    if (!quantity) return "";

    // Formato UMB
    const baseQuantity = quantity ?? 0;
    let formattedQuantity = `${baseQuantity.toFixed(3)}`;

    return formattedQuantity;
  }

  private formatQuantityUMP(quantity?: number, UMPACTIVE?: boolean): string {
    if (!quantity) return "";

    // Formato UMB
    let formattedQuantity = "";

    // Formato UMP si UMPACTIVE es true y existe
    if (UMPACTIVE && quantity) {
      const pararellQuantity = quantity ?? 0;
      formattedQuantity += `${pararellQuantity.toFixed(3)}`;
    }

    return formattedQuantity;
  }

  private transformData(lossList: lossInfo[]) {
    return lossList?.flatMap((item: lossInfo) => ({
      subRoute: get(item, "subRoute.name", ""),
      trip: get(item, "trip.id", 0),
      username: get(item, "user.user_login.username", ""),
      invoice: get(item, "sourceTransaction.serial", ""),
      serial: get(item, "serial", ""),
      createDateTimeDate: formatLuxonDate(get(item, "detail.createdAt"), "dd-MM-yyyy"),
      createDateTimeHour: formatLuxonDate(get(item, "detail.createdAt"), "hh:mm:ss a"),
      updateAuditAtDate: formatLuxonDate(get(item, "detail.updatedAt"), "dd-MM-yyyy"),
      updateAuditAtHour: formatLuxonDate(get(item, "detail.updatedAt"), "hh:mm:ss a"),
      soldToName: get(item, "soldTo.name", ""),
      materialCode: get(item, "detail.material.code", ""),
      materialName: get(item, "detail.material.name", ""),
      deliveredUMBUnit: item?.sourceTransaction?.baseUnitOfMeasure?.name,
      deliveredUMB: ExportTaskProcessor.types.number(
        this.formatQuantityUMB(item?.sourceTransaction?.baseQuantity)
      ),
      deliveredUMPUnit: item.sourceTransaction?.pararellUnitOfMeasure.name,
      deliveredUMP: ExportTaskProcessor.types.number(
        this.formatQuantityUMP(
          item.sourceTransaction?.pararellQuantity,
          this.UMPACTIVE
        )
      ),
      qtyMermaUMBUnit: item?.detail.baseUnitOfMeasure?.name,
      qtyMermaUMB: ExportTaskProcessor.types.number(
        this.formatQuantityUMB(item?.detail.baseQuantity)
      ),
      qtyMermaUMPUnit: this.UMPACTIVE
        ? item.detail?.pararellUnitOfMeasure?.name
        : "",
      qtyMermaUMP: ExportTaskProcessor.types.number(
        this.formatQuantityUMP(item.detail?.pararellQuantity, this.UMPACTIVE)
      ),
      percentageMerma: ExportTaskProcessor.types.number(
        Math.floor(
          (item.detail.baseQuantity / item.sourceTransaction.baseQuantity) *
            100 *
            100
        ) / 100 || 0
      ),
      netPrice: ExportTaskProcessor.types.number(
        get(item, "detail.unitPrice", 0)
      ),
      netValue: ExportTaskProcessor.types.number(get(item, "detail.total", 0)),
      comment: get(item, "detail.note", ""),
      reasonRejections: get(
        item,
        "detail.rejectedQuantitiesOnDelivery[0].reasonForRejection.name",
        ""
      ),
    }));
  }

  export(
    format: ExportFileFormat,
    range: ExportRange,
    totalItems: number,
    pageSize: number,
    pageIndex: number,
    exportId: string,
    exportTitle: string,
    properties: typeof LOSS_PROPERTIES,
    UMPACTIVE: boolean = false,
    filters: IFilters,
    optionsFilters: IPagination<lossInfo>
  ) {
    this.UMPACTIVE = UMPACTIVE;

    this.exportService
      .task({
        id: exportId,
        title: exportTitle,
        format,
        iterations: 1,
        start: (ctx: ExportContext) => {
          ctx.processor.localizeItemProperties(
            exportTitle,
            properties ?? LOSS_PROPERTIES
          );
        },
        next: (ctx: ExportContext) => {
          optionsFilters.limitPerPage =
            range === ExportRange.AllPages ? totalItems : pageSize;
          optionsFilters.currentPage =
            range === ExportRange.AllPages ? 1 : pageIndex;

          this.orderCustomerService
            .findAllWithCountShrinkages(optionsFilters, filters)
            .pipe(takeUntil(ctx.stopped))
            .subscribe((response) => {
              const formattedData = response?.data?.data.flatMap(
                (item: lossInfo) => {
                  const formattedDetail = { ...item.detail };

                  CURRENCYFIELDS.forEach((field) => {
                    const value = formattedDetail[field];
                    if (value !== undefined && value !== null) {
                      const numericValue = Number(value);
                      if (!isNaN(numericValue)) {
                        formattedDetail[field] = FORMATTER.format(numericValue);
                      }
                    }
                  });

                  const itemForExport = {
                    ...item,
                    detail: formattedDetail,
                  };

                  return itemForExport;
                }
              );

              const lossList = formattedData;
              const rows = this.transformData(lossList);

              ctx.processor.addToSection(exportTitle, rows);
              ctx.next();
            });
        },
      })
      .start();
  }

  canStartTask(exportId: string): boolean {
    if (this.exportService.taskIsActive(exportId)) {
      this.exportService.setVisible(true);
      return false;
    }
    return true;
  }
}
